import type { Logger } from "@expert/logging";

import { sendErrorMetric, sendEventMetric } from "@expert/cloudwatch-metrics";

/**
 * Bounded page-reload recovery for the ACGR "Invalid region us-west-2" ValidationException (#7073).
 *
 * With Amazon Connect Global Resiliency dual-region sign-in enabled, CCP init intermittently rejects
 * with an AWS ValidationException — a us-west-2-scoped request issued through a client bound to
 * us-east-1. A full page reload usually recovers (the failure is transient per attempt), so we reload
 * instead of dead-ending on the "Failed to create Agent SDK" screen.
 *
 * Two safeguards keep this from becoming a reload loop:
 *  - a hard cap of MAX_RELOAD_ATTEMPTS reloads, after which the error page is surfaced as before;
 *  - a WINDOW_MS window, so a stale counter from an earlier bad spell can't suppress recovery on a
 *    later, unrelated load. The counter is also cleared outright on a successful SDK load.
 *
 * NOTE: this is expert-facing recovery, NOT a fix — it does not reduce the underlying
 * ValidationException rate (an ACGR-side concern). Do not measure it against that metric.
 */

/**
 * sessionStorage key holding the serialized {@link RecoveryState}. sessionStorage (not localStorage)
 * because `initCCP` and its failure are per browsing context: the counter must survive reloads but
 * stay scoped to the tab, so two workspace tabs can't consume or clear each other's reload allowance.
 */
const ATTEMPT_KEY = "com.expert.ccpInitRecovery";

/** Max automatic reloads before we stop and show the error page. */
const MAX_RELOAD_ATTEMPTS = 2;

/** Attempts older than this are treated as a fresh start, so a stale counter can't disable recovery. */
const WINDOW_MS = 60_000;

interface RecoveryState {
    count: number;
    firstAt: number;
}

const EMPTY_STATE: RecoveryState = { count: 0, firstAt: 0 };

function safeStringify(value: unknown): string {
    try {
        return JSON.stringify(value) ?? "";
    } catch {
        return "";
    }
}

/** Reads a field only when it's actually a string, avoiding "[object Object]" coercions. */
function asString(value: unknown): string {
    return typeof value === "string" ? value : "";
}

function readState(): RecoveryState {
    try {
        const raw = sessionStorage.getItem(ATTEMPT_KEY);
        if (!raw) return EMPTY_STATE;
        const parsed = JSON.parse(raw) as Partial<RecoveryState>;
        const count = typeof parsed.count === "number" ? parsed.count : 0;
        const firstAt = typeof parsed.firstAt === "number" ? parsed.firstAt : 0;
        // Expired window → forget the old attempts.
        if (Date.now() - firstAt > WINDOW_MS) return EMPTY_STATE;
        return { count, firstAt };
    } catch {
        return EMPTY_STATE;
    }
}

/**
 * True for the ACGR region ValidationException we want to auto-recover from. The AWS SDK surfaces it
 * through Streams getEndpoints / ACGR sign-in with a message like "Invalid region us-west-2! This
 * service can only handle requests for region us-east-1".
 *
 * We require that "invalid region" signature rather than matching bare `ValidationException`: only a
 * region mismatch is transient enough that a reload helps. An unrelated (often permanent) validation
 * failure must fall through to the existing error page instead of burning two pointless reloads.
 * Both object rejections (via `message`/nested fields) and string rejections are handled.
 */
export function isCcpRegionValidationError(error: unknown): boolean {
    if (error == null) return false;

    let haystack: string;
    if (typeof error === "string") {
        haystack = error;
    } else if (typeof error === "object") {
        const record = error as Record<string, unknown>;
        // Read message explicitly (Error.message is non-enumerable, so JSON.stringify misses it) and
        // include the serialized error to catch a wrapped/nested region signature.
        haystack = `${asString(record.message)} ${safeStringify(error)}`;
    } else {
        return false;
    }

    return /invalid region/i.test(haystack);
}

/**
 * Records a recovery attempt and triggers a full page reload, unless the reload cap (within the
 * recovery window) has been reached. Returns true if a reload was triggered — callers should stop and
 * let the navigation happen; false means the cap is exhausted (or the counter can't be persisted) and
 * the caller should surface the error as usual.
 *
 * A full reload (not an in-page retry) is required: `connect.core.initCCP` is once-per-browsing-context
 * in Streams v2 and `connect.core.terminate()` does not reset it, so re-running create() in the same
 * page cannot re-initialize CCP.
 */
export function attemptCcpInitReload(logger: Logger, logContext: Record<string, unknown> = {}): boolean {
    const { count, firstAt } = readState();
    if (count >= MAX_RELOAD_ATTEMPTS) {
        // Auto-recovery exhausted — the expert is dead-ended on the error page despite the reloads.
        // This is the expert-impacting signal worth alarming on (distinct from the generic
        // RuntimeErrorLogged that the error log below also emits via the logger onError hook).
        void sendErrorMetric("CcpInitRegionReloadExhausted");
        logger.warn(
            { ...logContext, attempts: count, maxAttempts: MAX_RELOAD_ATTEMPTS },
            "CCP init region ValidationException — reload cap reached; surfacing error",
        );
        return false;
    }

    const next: RecoveryState = { count: count + 1, firstAt: firstAt || Date.now() };
    try {
        sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(next));
    } catch {
        // Can't persist the counter → can't bound reloads safely → don't reload.
        logger.warn(logContext, "CCP init recovery: unable to persist reload counter; skipping auto-reload");
        return false;
    }

    // Trend counter for how often auto-recovery fires — a rising rate is an early warning that the
    // ACGR ValidationException storm is back/worsening. `attempt` (1..MAX_RELOAD_ATTEMPTS) is a
    // bounded dimension. Emitted before reload; sendMetrics uses keepalive so it survives the unload.
    void sendEventMetric("CcpInitRegionAutoReload", { attempt: String(next.count) });
    logger.warn(
        { ...logContext, attempt: next.count, maxAttempts: MAX_RELOAD_ATTEMPTS },
        "CCP init region ValidationException — auto-reloading to recover",
    );
    globalThis.location.reload();
    return true;
}

/** Clears the recovery counter after a successful load so future failures start from a clean slate. */
export function clearCcpInitRecoveryAttempts(): void {
    try {
        sessionStorage.removeItem(ATTEMPT_KEY);
    } catch {
        // best effort
    }
}
